import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { Navigate, useSearchParams, Link } from 'react-router-dom';
import CandidateSearchFilters from '../components/Skelbimai/CandidateSearchFilters';
import CandidateGrid from '../components/Skelbimai/CandidateGrid';
import JobPickerModal from '../components/Skelbimai/JobPickerModal';
import { Grid, List, UserPlus, ChevronLeft, ChevronRight } from 'lucide-react';
import './Skelbimai.scss';

interface Candidate {
  id: number;
  user_id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  address: string;
  description: string;
  work_experience: any[];
  education: any[];
  language_skills: any[];
  job_preferences: any;
  cv_visibility_restriction: boolean;
  created_at: string;
  updated_at: string;
}

export interface FilterOptions {
  searchKeyword: string;
  keywords: string[]; // Keyword chips
  location: string[];
  experience: string[];
  education: string[];
  languages: string[];
  computerSkills: string[];
  drivingLicense: string[];
  salary: string;
  workType: string[];
  age: string;
  gender: string[];
  status: string[];
  activity: string;
  cvLanguage: string[];
  hasPhoto: boolean;
  hasRecommendation: boolean;
  // Company-related filters
  companyNames: string[];
  lastWorkField: string[];
  companyActivity: string[];
}

// Convert frontend filters to backend query params
const filtersToQueryParams = (filters: FilterOptions): URLSearchParams => {
  const params = new URLSearchParams();
  
  // Keywords
  if (filters.searchKeyword) {
    params.set('query', filters.searchKeyword);
  }
  if (filters.keywords.length > 0) {
    params.set('keywords', filters.keywords.join(','));
  }
  
  // Location
  if (filters.location.length > 0) {
    params.set('location', filters.location.join(','));
  }
  
  // Age (parse from "16-65" format)
  if (filters.age && filters.age !== '16-65') {
    const [min, max] = filters.age.split('-').map(Number);
    if (min > 16) params.set('ageMin', min.toString());
    if (max < 65) params.set('ageMax', max.toString());
  }
  
  // Gender
  if (filters.gender.length > 0) {
    params.set('gender', filters.gender.join(','));
  }
  
  // CV Language
  if (filters.cvLanguage.length > 0) {
    params.set('cvLanguage', filters.cvLanguage.join(','));
  }
  
  // Boolean filters
  if (filters.hasPhoto) {
    params.set('hasPhoto', 'true');
  }
  if (filters.hasRecommendation) {
    params.set('hasRecommendation', 'true');
  }
  
  // Activity
  if (filters.activity) {
    params.set('activity', filters.activity);
  }
  
  // Status
  if (filters.status.length > 0) {
    params.set('status', filters.status.join(','));
  }
  
  // Education
  if (filters.education.length > 0) {
    params.set('education', filters.education.join(','));
  }
  
  // Languages
  if (filters.languages.length > 0) {
    params.set('languages', filters.languages.join(','));
  }
  
  // Computer skills
  if (filters.computerSkills.length > 0) {
    params.set('computerSkills', filters.computerSkills.join(','));
  }
  
  // Driving license
  if (filters.drivingLicense.length > 0) {
    params.set('drivingLicense', filters.drivingLicense.join(','));
  }
  
  // Work type
  if (filters.workType.length > 0) {
    params.set('workType', filters.workType.join(','));
  }
  
  // Salary
  if (filters.salary) {
    params.set('salaryMin', filters.salary);
  }
  
  // Experience (work fields)
  if (filters.experience.length > 0) {
    params.set('workFields', filters.experience.join(','));
  }
  
  // Company names
  if (filters.companyNames?.length > 0) {
    params.set('companyNames', filters.companyNames.join(','));
  }
  
  // Last work field
  if (filters.lastWorkField?.length > 0) {
    params.set('lastWorkField', filters.lastWorkField.join(','));
  }
  
  // Company activity
  if (filters.companyActivity?.length > 0) {
    params.set('companyActivity', filters.companyActivity.join(','));
  }
  
  return params;
};

const Skelbimai: React.FC = () => {
  const { user, isAuthenticated, isLoading } = useAuth();
  const { showToast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [loading, setLoading] = useState(true);
  const [gridLayout, setGridLayout] = useState<'grid-3' | 'grid-2' | 'list'>('grid-3');
  const [showFilters, setShowFilters] = useState(() => window.innerWidth >= 768);
  const [filtersClosing, setFiltersClosing] = useState(false);

  const handleCollapseFilters = useCallback(() => {
    setFiltersClosing(true);
    setTimeout(() => {
      setShowFilters(false);
      setFiltersClosing(false);
    }, 250);
  }, []);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalResults, setTotalResults] = useState(0);
  const [liveCount, setLiveCount] = useState<number | null>(null);
  const [countLoading, setCountLoading] = useState(false);
  const [hideInApplications, setHideInApplications] = useState(false);
  const [selectedCandidates, setSelectedCandidates] = useState<Set<number>>(new Set());
  const [showJobPicker, setShowJobPicker] = useState(false);
  const candidatesPerPage = 20;
  const countDebounceRef = useRef<NodeJS.Timeout | null>(null);
  
  // Non-authenticated users see limited candidates
  const isGuest = !isAuthenticated;
  const guestCandidateLimit = 6;

  // Get initial filters from URL parameters (URL is the source of truth for filter state)
  const getInitialFilters = (): FilterOptions => {
    const baseFilters: FilterOptions = {
      searchKeyword: '',
      keywords: [],
      location: [],
      experience: [],
      education: [],
      languages: [],
      computerSkills: [],
      drivingLicense: [],
      salary: '',
      workType: [],
      age: '',
      gender: [],
      status: [],
      activity: '',
      cvLanguage: [],
      hasPhoto: false,
      hasRecommendation: false,
      companyNames: [],
      lastWorkField: [],
      companyActivity: []
    };
    
    // Read ALL filters from URL params (URL is the source of truth)
    const queryParam = searchParams.get('query');
    const keywordsParam = searchParams.get('keywords');
    const locationParam = searchParams.get('location');
    const educationParam = searchParams.get('education');
    const languagesParam = searchParams.get('languages');
    const computerSkillsParam = searchParams.get('computerSkills');
    const drivingLicenseParam = searchParams.get('drivingLicense');
    const workTypeParam = searchParams.get('workType');
    const workFieldsParam = searchParams.get('workFields');
    const genderParam = searchParams.get('gender');
    const cvLanguageParam = searchParams.get('cvLanguage');
    const ageMinParam = searchParams.get('ageMin');
    const ageMaxParam = searchParams.get('ageMax');
    const activityParam = searchParams.get('activity');
    const statusParam = searchParams.get('status');
    const salaryMinParam = searchParams.get('salaryMin');
    const hasPhotoParam = searchParams.get('hasPhoto');
    const hasRecommendationParam = searchParams.get('hasRecommendation');
    const companyNamesParam = searchParams.get('companyNames');
    const lastWorkFieldParam = searchParams.get('lastWorkField');
    const companyActivityParam = searchParams.get('companyActivity');
    
    if (queryParam) baseFilters.searchKeyword = queryParam;
    if (keywordsParam) baseFilters.keywords = keywordsParam.split(',').map(k => k.trim()).filter(k => k);
    if (locationParam) baseFilters.location = locationParam.split(',').map(l => decodeURIComponent(l));
    if (educationParam) baseFilters.education = educationParam.split(',');
    if (languagesParam) baseFilters.languages = languagesParam.split(',');
    if (computerSkillsParam) baseFilters.computerSkills = computerSkillsParam.split(',');
    if (drivingLicenseParam) baseFilters.drivingLicense = drivingLicenseParam.split(',');
    if (workTypeParam) baseFilters.workType = workTypeParam.split(',');
    if (workFieldsParam) baseFilters.experience = workFieldsParam.split(',');
    if (genderParam) baseFilters.gender = genderParam.split(',');
    if (cvLanguageParam) baseFilters.cvLanguage = cvLanguageParam.split(',');
    if (activityParam) baseFilters.activity = activityParam;
    if (statusParam) baseFilters.status = statusParam.split(',');
    if (salaryMinParam) baseFilters.salary = salaryMinParam;
    if (hasPhotoParam === 'true') baseFilters.hasPhoto = true;
    if (hasRecommendationParam === 'true') baseFilters.hasRecommendation = true;
    if (companyNamesParam) baseFilters.companyNames = companyNamesParam.split(',');
    if (lastWorkFieldParam) baseFilters.lastWorkField = lastWorkFieldParam.split(',');
    if (companyActivityParam) baseFilters.companyActivity = companyActivityParam.split(',');
    
    // Build age string from min/max
    if (ageMinParam || ageMaxParam) {
      const minAge = ageMinParam || '16';
      const maxAge = ageMaxParam || '65';
      baseFilters.age = `${minAge}-${maxAge}`;
    }
    
    return baseFilters;
  };

  const [filters, setFilters] = useState<FilterOptions>(getInitialFilters());

  // Fetch live count from backend (debounced)
  const fetchLiveCount = useCallback(async (filterOptions: FilterOptions) => {
    if (countDebounceRef.current) {
      clearTimeout(countDebounceRef.current);
    }

    countDebounceRef.current = setTimeout(async () => {
      try {
        setCountLoading(true);
        const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
        const token = localStorage.getItem('auth_token');
        
        const queryParams = filtersToQueryParams(filterOptions);
        if (hideInApplications && !isGuest) {
          queryParams.set('hideInApplications', 'true');
        }
        
        const endpoint = isGuest 
          ? `${API_BASE_URL}/cv/public/count`
          : `${API_BASE_URL}/cv/employer/count`;
        
        const url = `${endpoint}?${queryParams.toString()}`;
        
        const headers: Record<string, string> = {
          'Content-Type': 'application/json'
        };
        
        if (!isGuest && token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
        
        const response = await fetch(url, { headers });
        
        if (response.ok) {
          const result = await response.json();
          setLiveCount(result.count);
        }
      } catch (error) {
        console.error('Error fetching count:', error);
      } finally {
        setCountLoading(false);
      }
    }, 300);
  }, [isGuest, hideInApplications]);

  // Fetch candidates from backend with ALL filters
  const fetchCandidates = useCallback(async (filterOptions: FilterOptions, page: number = 1, hideInApp?: boolean) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('auth_token');
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      
      const queryParams = filtersToQueryParams(filterOptions);
      queryParams.set('page', page.toString());
      queryParams.set('limit', candidatesPerPage.toString());
      
      const shouldHide = hideInApp ?? hideInApplications;
      if (shouldHide && !isGuest) {
        queryParams.set('hideInApplications', 'true');
      }
      
      const endpoint = isGuest
        ? `${API_BASE_URL}/cv/public/preview`
        : `${API_BASE_URL}/cv/employer/search`;
      
      const url = `${endpoint}?${queryParams.toString()}`;
      
      const headers: Record<string, string> = {
        'Content-Type': 'application/json'
      };
      
      if (!isGuest && token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(url, { headers });

      if (response.ok) {
        const result = await response.json();
        const allCandidates = result.data || [];
        const total = result.pagination?.total || result.total || allCandidates.length;
        
        setCandidates(allCandidates);
        setTotalResults(total);
        setLiveCount(total);
      } else {
        const errorData = await response.json();
        console.error('Failed to fetch candidates:', errorData.message);
        setCandidates([]);
        setTotalResults(0);
      }
    } catch (error) {
      console.error('Error fetching candidates:', error);
      setCandidates([]);
      setTotalResults(0);
    } finally {
      setLoading(false);
    }
  }, [isGuest, hideInApplications]);

  // Apply filters - fetch from backend with all filters
  const applyFilters = useCallback((newFilters: FilterOptions) => {
    console.log('🔍 Applying filters:', newFilters);
    
    // Update URL with all filters (page reset to 1 by omitting page param)
    const newParams = filtersToQueryParams(newFilters);
    // Preserve mode param if present
    const mode = searchParams.get('mode');
    if (mode) newParams.set('mode', mode);
    
    setFilters(newFilters);
    setSearchParams(newParams, { replace: true });
    // useEffect on searchParams change handles the fetch
  }, [searchParams, setSearchParams]);

  // Handle filter preview (just update count without fetching full results)
  const previewFilters = useCallback((newFilters: FilterOptions) => {
    fetchLiveCount(newFilters);
  }, [fetchLiveCount]);

  const getCurrentPageCandidates = () => {
    // For guests, limit to 6 candidates total
    if (isGuest) {
      return candidates.slice(0, guestCandidateLimit);
    }
    
    // Backend already returns paginated results
    return candidates;
  };

  const handlePageChange = (page: number) => {
    setSelectedCandidates(new Set());
    window.scrollTo({ top: 0, behavior: 'smooth' });
    // Persist page in URL — triggers useEffect which fetches the right page
    // and ensures returnUrl captures the page for back navigation
    const newParams = new URLSearchParams(searchParams.toString());
    if (page === 1) {
      newParams.delete('page');
    } else {
      newParams.set('page', page.toString());
    }
    setSearchParams(newParams, { replace: true });
  };

  const totalPages = Math.ceil(totalResults / candidatesPerPage);

  // Initial fetch with URL parameters
  useEffect(() => {
    const initialFilters = getInitialFilters();
    setFilters(initialFilters);
    
    // Read page from URL (restored when navigating back from candidate detail)
    const pageFromUrl = parseInt(searchParams.get('page') || '1');
    setCurrentPage(pageFromUrl);
    fetchCandidates(initialFilters, pageFromUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]); // Re-fetch when URL params change

  // Show loading state while auth is being checked
  if (isLoading) {
    return (
      <div className="skelbimai-container">
        <div className="loading">Kraunama...</div>
      </div>
    );
  }

  if (isAuthenticated && user?.user_type !== 'employer') {
    return <Navigate to="/darbo-skelbimai" replace />;
  }

  if (loading) {
    return (
      <div className="skelbimai-page">
        <div className="skelbimai-loading">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Kraunami kandidatai...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="skelbimai-page">
      {/* Main Content Area */}
      <div className="skelbimai-content">
        {/* Filters Sidebar */}
        {showFilters && (
          <div className={`filter-sidebar-employer${filtersClosing ? ' closing' : ''}`}>
            <div className="filter-sidebar-inner">
              <CandidateSearchFilters 
                isVisible={showFilters}
                onFiltersChange={applyFilters}
                onFiltersPreview={previewFilters}
                onCollapse={handleCollapseFilters}
                currentFilters={filters}
                liveCount={liveCount}
                countLoading={countLoading}
              />
            </div>
          </div>
        )}
        
        {/* Main Content */}
        <div className="main-content">
          {/* Content Header with Title and Controls */}
          <div className="content-header-bar">
            <div className="header-left">
              {!showFilters && (
                <button
                  className="sidebar-reopen-btn"
                  onClick={() => setShowFilters(true)}
                  aria-label="Atidaryti filtrus"
                >
                  <ChevronRight size={16} />
                </button>
              )}
              <div>
                <h2>Kandidatų CV ({totalResults})</h2>
                <div className="breadcrumb">
                  <span>Titulinis</span>
                  <span>›</span>
                  <span>Nauja paieška</span>
                </div>
              </div>
            </div>

            <div className="header-controls">
              {!isGuest && (
                <div className="control-group">
                  <button 
                    className={`action-btn ${selectedCandidates.size === candidates.length && candidates.length > 0 ? 'active' : ''}`}
                    onClick={() => {
                      if (selectedCandidates.size === candidates.length && candidates.length > 0) {
                        setSelectedCandidates(new Set());
                      } else {
                        setSelectedCandidates(new Set(candidates.map(c => c.id)));
                      }
                    }}
                  >
                    Žymėti visus
                  </button>
                  <button 
                    className="action-btn"
                    disabled={selectedCandidates.size === 0}
                    onClick={() => setShowJobPicker(true)}
                  >
                    Įkelti į atrankas
                  </button>
                </div>
              )}
              
              {!isGuest && (
                <div className="sort-controls">
                  <select
                    value={hideInApplications ? 'hide' : 'show'}
                    onChange={(e) => {
                      const hide = e.target.value === 'hide';
                      setHideInApplications(hide);
                      setCurrentPage(1);
                      fetchCandidates(filters, 1, hide);
                    }}
                  >
                    <option value="show">Rodyti visus</option>
                    <option value="hide">Nerodyti įkeltų į atrankas</option>
                  </select>
                </div>
              )}

              <div className="view-controls">
                <div className="pagination-mini">
                  <span>{currentPage} iš {totalPages || 1}</span>
                  <button
                    className="page-arrow"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage <= 1}
                  >
                    <ChevronLeft size={16} />
                  </button>
                  <button
                    className="page-arrow"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage >= totalPages}
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>
                
                <button 
                  className={`layout-btn ${gridLayout === 'grid-3' ? 'active' : ''}`}
                  onClick={() => setGridLayout('grid-3')}
                  title="3 stulpeliai"
                >
                  <Grid size={16} />
                </button>
                <button 
                  className={`layout-btn ${gridLayout === 'grid-2' ? 'active' : ''}`}
                  onClick={() => setGridLayout('grid-2')}
                  title="2 stulpeliai"
                >
                  <Grid size={16} />
                </button>
                <button 
                  className={`layout-btn ${gridLayout === 'list' ? 'active' : ''}`}
                  onClick={() => setGridLayout('list')}
                  title="Sąrašas"
                >
                  <List size={16} />
                </button>
                
              </div>
            </div>
          </div>

          <CandidateGrid
            candidates={getCurrentPageCandidates()}
            layout={gridLayout}
            loading={loading}
            isGuest={isGuest}
            selectedCandidates={selectedCandidates}
            onToggleSelect={(id) => {
              setSelectedCandidates(prev => {
                const next = new Set(prev);
                if (next.has(id)) {
                  next.delete(id);
                } else {
                  next.add(id);
                }
                return next;
              });
            }}
          />

          {/* Guest Register CTA */}
          {isGuest && totalResults > guestCandidateLimit && (
            <div className="guest-register-cta">
              <div className="cta-content">
                <UserPlus size={32} />
                <div className="cta-text">
                  <h3>Norite matyti daugiau kandidatų?</h3>
                  <p>
                    Rasta <strong>{totalResults}</strong> kandidatų. 
                    Registruokitės ir gaukite prieigą prie visų kandidatų profilių.
                  </p>
                </div>
                <Link to="/register?type=employer" className="btn-register">
                  Registruotis
                </Link>
              </div>
            </div>
          )}
          
          {/* Pagination Footer - only for authenticated users */}
          {!isGuest && totalPages > 1 && (
            <div className="pagination">
              <span className="page-info">
                {currentPage} iš {totalPages}
              </span>

              <div className="page-controls">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="page-btn"
                >
                  ‹
                </button>

                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum: number;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`page-number ${currentPage === pageNum ? 'active' : ''}`}
                    >
                      {pageNum}
                    </button>
                  );
                })}

                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="page-btn"
                >
                  ›
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {!isGuest && (
        <JobPickerModal
          isOpen={showJobPicker}
          cvIds={Array.from(selectedCandidates)}
          onClose={() => setShowJobPicker(false)}
          onSuccess={(added, skipped) => {
            setShowJobPicker(false);
            setSelectedCandidates(new Set());
            if (hideInApplications) {
              fetchCandidates(filters, currentPage);
            }
            showToast(`Pridėta: ${added}${skipped > 0 ? `, praleista (jau egzistuoja): ${skipped}` : ''}`, 'success');
          }}
        />
      )}
    </div>
  );
};

export default Skelbimai;
